"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

/**
 * Starts every new page at the top.
 *
 * The App Router does this itself, but not dependably on these routes. The
 * program and workshop pages each have a `loading.tsx`, so a navigation first
 * swaps in a short skeleton — the document collapses, the browser clamps the
 * scroll offset to the new maximum, and the real content then streams in and
 * makes the page tall again. Whether the router's own scroll lands before or
 * after that is a matter of timing, which is why someone reading the bottom of
 * a long page and picking another program from the burger menu *sometimes*
 * arrives part-way down instead of at the top.
 *
 * Two details matter here:
 * - `html { scroll-behavior: smooth }` in globals.css would animate this jump,
 *   and an animation that starts while the next page is still streaming is
 *   exactly what gets interrupted. The property is forced to `auto` for the
 *   duration and then put back, so in-page anchors keep their smooth scroll.
 * - Back and forward are left alone. The browser restoring your old position is
 *   the correct behaviour there, so a popstate navigation is skipped.
 */
export default function ScrollToTopOnNavigate() {
  const pathname = usePathname();
  const cameFromHistory = useRef(false);

  useEffect(() => {
    const markPop = () => {
      cameFromHistory.current = true;
    };
    window.addEventListener("popstate", markPop);
    return () => window.removeEventListener("popstate", markPop);
  }, []);

  useEffect(() => {
    if (cameFromHistory.current) {
      cameFromHistory.current = false;
      return;
    }

    // A link to a section on the new page names its own target.
    if (window.location.hash) return;

    const html = document.documentElement;
    const previous = html.style.scrollBehavior;
    html.style.scrollBehavior = "auto";
    window.scrollTo(0, 0);
    html.style.scrollBehavior = previous;
  }, [pathname]);

  return null;
}
